import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useReference } from "@/lib/useReference";
import ConfirmDialog from "@/components/ConfirmDialog";
import PageHeader, { EmptyState } from "@/components/PageHeader";

export default function Campuses({ embedded = false }) {
  const { ref, reloadRef } = useReference();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", district_id: "" });
  const [del, setDel] = useState(null);
  const [filterDistrict, setFilterDistrict] = useState("all");

  const load = useCallback(async () => { const { data } = await api.get("/admin/campuses"); setItems(data.items); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", code: "", district_id: ref.districts[0]?.id || "" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, code: c.code, district_id: c.district_id }); setOpen(true); };

  const save = async () => {
    if (!form.district_id) { toast.error("Select a district"); return; }
    try {
      if (editing) await api.put(`/admin/campuses/${editing.id}`, form);
      else await api.post("/admin/campuses", form);
      toast.success("Saved"); setOpen(false); load(); reloadRef();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/campuses/${del.id}`); toast.success("Deleted"); setDel(null); load(); reloadRef(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  const shown = items.filter((c) => filterDistrict === "all" || c.district_id === filterDistrict);

  return (
    <div>
      {embedded ? (
        <div className="mb-4 flex justify-end"><Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-campus-btn"><Plus className="mr-2 h-4 w-4" /> Add campus</Button></div>
      ) : (
        <PageHeader title="Campuses" subtitle="Manage campuses"
          actions={<Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-campus-btn"><Plus className="mr-2 h-4 w-4" /> Add campus</Button>} />
      )}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">District</Label>
        <Select value={filterDistrict} onValueChange={setFilterDistrict}>
          <SelectTrigger className="w-56" data-testid="campus-district-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All districts</SelectItem>
            {ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {shown.length === 0 ? <EmptyState title="No campuses" testId="campuses-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <Table data-testid="campuses-table">
            <TableHeader><TableRow className="bg-slate-50">{["Name", "Code", "District", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {shown.map((c) => (
                <TableRow key={c.id} data-testid={`campus-row-${c.code}`}>
                  <TableCell className="font-semibold text-slate-800">{c.name}</TableCell>
                  <TableCell className="font-mono text-sm">{c.code}</TableCell>
                  <TableCell className="text-sm text-slate-500">{c.district_name}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-campus-${c.code}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-red-500" onClick={() => setDel(c)} data-testid={`delete-campus-${c.code}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="campus-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit campus" : "Add campus"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="campus-name-input" /></div>
            <div className="space-y-2"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="campus-code-input" /></div>
            <div className="space-y-2"><Label>District</Label>
              <Select value={form.district_id} onValueChange={(v) => setForm((f) => ({ ...f, district_id: v }))}>
                <SelectTrigger data-testid="campus-district-select"><SelectValue placeholder="District" /></SelectTrigger>
                <SelectContent>{ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="campus-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete campus?" description={del ? `${del.name} will be removed.` : ""} confirmText="Delete" destructive onConfirm={remove} />
    </div>
  );
}
